
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { appUrls } from "../../config/api-config/appUrls";

type Member = {
    content?: {
        membership: string;
        displayname?: string;
    };
    state_key: string;
  };

type MembersData = {
    chunk?: Member[];
  };

type Device = {
    device_id: string;
    display_name?: string;
    last_seen_ts?: number;
};

type Presence = {
    presence: string | null;
    lastActiveAgo: number | null;
    statusMsg: string | null;
};

type UserDetails = {
    userId: string;
    displayName: string | null;
    avatarUrl: string | null;
    presence: Presence | null;
    accountData: any | null;
    devices: Device[] | null; // <-- allow null
    roomMembership: string | null;
  };

const homeserverUrl = process.env.REACT_APP_MATRIX_HOMESERVER_URL;

export async function fetchRoomDetails(roomId:string, accessToken:string){
  const RoomID = `!${roomId}:convaychat.convay.com`
  // Fetch Room Name
    const roomNameUrl = `${process.env.matrixHomeserver ? process.env.matrixHomeserver : homeserverUrl}${appUrls.matrix.rooms}${RoomID}/state/m.room.name`;
  try {
    const nameResponse = await fetch(roomNameUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    if (nameResponse.ok) {
      const nameData = await nameResponse.json();

      return nameData
    } else {
      // It's common for a room not to have a name, which might return 404 or specific Matrix error
      // We'll log the specific error if it's not found
      const errorDetails = await nameResponse.json().catch(() => nameResponse.text());
      if (nameResponse.status === 404) {
        console.error("Name: [Not Set (404)]");
      } else if (errorDetails && errorDetails.errcode === 'M_NOT_FOUND') {
          console.error("Name: [Not Set (M_NOT_FOUND)]", errorDetails.errcode);
      } else {
        console.warn(`  Failed to fetch name (${nameResponse.status} ${nameResponse.statusText}):`, errorDetails);
      }
    }
  } catch (error) {
    console.error(`  Error fetching name for ${RoomID}:`, error);
  }
}

// --- Function to fetch comprehensive user information ---
async function fetchUserDetails(userId: string, accessToken: string) {
    const userInfo = {
        userId: userId,
        displayName: null,
        avatarUrl: null,
        presence: {
            presence: null,
            lastActiveAgo: null,
            statusMsg: null
        },
        accountData: null,
        devices: null,
        roomMembership: null
    };

    try {
        // 1. Fetch user profile (display name and avatar)
        const profileUrl = `${homeserverUrl}${appUrls.matrix.profile}${userId}`;
        const profileResponse = await fetch(profileUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            userInfo.displayName = profileData.displayname;
            userInfo.avatarUrl = profileData.avatar_url;
        }

        // 2. Fetch user presence (if available)
        try {
            const presenceUrl = `${homeserverUrl}${appUrls.matrix.presence}${userId}/status`;
            const presenceResponse = await fetch(presenceUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (presenceResponse.ok) {
                const presenceData = await presenceResponse.json();
                userInfo.presence = {
                    presence: presenceData.presence,
                    lastActiveAgo: presenceData.last_active_ago,
                    statusMsg: presenceData.status_msg
                };
            }
        } catch (error) {
            console.error(`Presence info not available for ${userId}`);
        }

        // 3. Fetch user's devices (if you have permission)
        try {
            const devicesUrl = `${homeserverUrl}${appUrls.matrix.devices}`;
            const devicesResponse = await fetch(devicesUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (devicesResponse.ok) {
                const devicesData = await devicesResponse.json();
                userInfo.devices = devicesData.devices;
            }
        } catch (error) {
            console.error(`Device info not available for ${userId}`);
        }

        // 4. Fetch account data (if it's the current user)
        try {
            const accountDataUrl = `${homeserverUrl}${appUrls.matrix.accountData}`;
            const accountDataResponse = await fetch(accountDataUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (accountDataResponse.ok) {
                const accountData = await accountDataResponse.json();
                userInfo.accountData = accountData;
            }
        } catch (error) {
            console.error(`Account data not available for ${userId}`);
        }

        return userInfo;
    } catch (error) {
        console.error(`  Error fetching user details for ${userId}:`, error);
        return userInfo;
    }
}

// --- Function to fetch detailed participant information for 1-1 rooms ---
async function fetchDetailedParticipantInfo(roomId: string, accessToken: string) {
    try {
        // First, get the room members
        const membersUrl = `${homeserverUrl}${appUrls.matrix.rooms}${roomId}/members`;
        const membersResponse = await fetch(membersUrl, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });

        if (!membersResponse.ok) {
            throw new Error(`Failed to fetch room members: ${membersResponse.status}`);
        }

        const membersData: MembersData = await membersResponse.json();
        const members = membersData.chunk || [];

        // Find all participants (including current user)
        const participants = members.filter(
            (member): member is Member & { content: { membership: string } } =>
                !!member.content && member.content.membership === 'join'
          );

        if (participants.length === 0) {
            return '[No Participants Found]';
        }

        return participants;
    } catch (error) {
        console.error(`  Error fetching detailed participant info:`, error);
        return '[Error fetching detailed participants]';
    }
}

// --- Function to fetch all joined rooms and their details ---
export async function fetchAllRoomDetails(accessToken: string, roomId: string) {

    const fullRoomId = `!${roomId}:convaychat.convay.com`

    const jwtToken = Cookies.get("auth_token");
    const decodedToken: any = jwtToken ? jwt_decode(jwtToken) : null;
    const userId = decodedToken?.user_id;

    const fetchedValue = await fetchDetailedParticipantInfo(fullRoomId, accessToken);
    const updatedParticipants = Array.isArray(fetchedValue)
        ? fetchedValue.map((participant: any) => ({
            ...participant,
            isMe: participant.user_id === userId,
        }))
        : [];

    return updatedParticipants
}
